import { alphaSorter } from 'vet-bones/utils';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import {
  PublishProjectResponse,
  projectsActionCreators,
} from 'src/actions/projects/actionCreators';
import { FailureResponse, SuccessResponse } from 'src/actions/request/types';
import { DataSourceProperty } from 'src/constants/dataSourceProperties';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { Connection } from 'src/store/connections/types';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import * as copy from 'src/templates/copy';

type DetailsFailureResponse = FailureResponse<
  {
    connection: Connection;
    dataSourceId: string;
  },
  DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_FAILURE
>;
type DetailsSuccessResponse = SuccessResponse<
  {
    connection: Connection;
    dataSourceId: string;
  },
  any,
  DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_FAILURE
>;
type UpdateFailureResponse = FailureResponse<
  {
    dataSourceId: string;
    deltaOptions: Record<string, string>;
  },
  DataSourcesActionType.UPDATE_DATA_SOURCE_FAILURE
>;
type UpdateSuccessResponse = SuccessResponse<
  {
    dataSourceId: string;
    deltaOptions: Record<string, string>;
  },
  any,
  DataSourcesActionType.UPDATE_DATA_SOURCE_SUCCESS
>;

const parenthesesRegex = new RegExp(/\(([^)]+)\)/, 'g');

export const publishUniqueKeySets =
  ({
    connection,
    updatedDataSources,
    validClassMappingsByResource,
    virtualGraphsById,
  }: {
    connection: Connection | null;
    updatedDataSources: Set<string>;
    validClassMappingsByResource: Record<string, Mapping[]>;
    virtualGraphsById: Record<string, ProjectVirtualGraph>;
  }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    // to set valid unique key sets, we need to
    // 1) filter out vgs that were created using custom query ()
    // 2) get primary identifiers for each mapping for each filtered & published vg
    // 3) stringify values for these primary identifiers
    // 4) check against old unique.key.sets prop in data source to filter out duplicates
    // 5) actually update each data sources
    if (!connection) {
      return;
    }

    dispatch(projectsActionCreators.publishUniqueKeySetsStarted());

    // get details from data sources in current connection
    const dataSourcesForDetails = new Set<string>();
    Object.values(virtualGraphsById).forEach((projectVirtualGraph) => {
      const { dataSourceId } = projectVirtualGraph;

      if (updatedDataSources.has(dataSourceId)) {
        dataSourcesForDetails.add(dataSourceId);
      }
    });
    const detailsResponses = await Promise.all(
      Array.from(dataSourcesForDetails).map((dataSourceId) =>
        dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
          dataSourceId,
          connection
        )
      )
    );
    const failDetailsResponses: DetailsFailureResponse[] =
      detailsResponses.filter(
        (response): response is DetailsFailureResponse =>
          response.type ===
          DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_FAILURE
      );
    const successDetailsResponses: DetailsSuccessResponse[] =
      detailsResponses.filter(
        (response): response is DetailsSuccessResponse =>
          response.type ===
          DataSourcesActionType.REQUEST_DATA_SOURCE_OPTIONS_SUCCESS
      );

    // unique.key.sets look like
    // "(APP.CUSTOMERS.ID),(APP.CUSTOMERS.FNAME,APP.CUSTOMERS.LNAME)"
    // or "(SCHEMA.APP.CUSTOMERS.ID),(SCHEMA.APP.CUSTOMERS.FNAME,APP.CUSTOMERS.LNAME)"
    // while Stardog validates them against existing tables & columns,
    // they are not sorted, so we need to sort them before checking
    // if we could be adding duplicates (which causes an error if trying to add...)
    // ex: "(actor.actor_id,actor.first_name),(actor.first_name,actor.actor_id)"
    // WILL error
    const uniqueKeySetsByDataSource: Record<string, Set<string>> = {};
    successDetailsResponses.forEach((response) => {
      const options: Record<string, any> =
        response.payload?.response?.body?.options;
      const uniqueKeySets: string | undefined =
        options?.[DataSourceProperty.UNIQUE_KEY_SETS];

      const values: string[] = (
        uniqueKeySets ? uniqueKeySets.match(parenthesesRegex) || [] : []
      ).map((str) => str.substring(1, str.length - 1));

      const { dataSourceId } = response.payload.action;

      if (!uniqueKeySetsByDataSource[dataSourceId]) {
        uniqueKeySetsByDataSource[dataSourceId] = new Set();
      }
      values.forEach((value) => {
        const alphabetizedValue = value.split(',').sort(alphaSorter).join(',');
        uniqueKeySetsByDataSource[dataSourceId].add(alphabetizedValue);
      });
    });

    // no point updating data sources if nothing changes
    const dataSourceIdsToUpdate = new Set<string>();
    Object.values(virtualGraphsById).forEach((projectVirtualGraph) => {
      const { dataSourceId, from, id } = projectVirtualGraph;

      // from is string if vg uses custom query
      if (typeof from === 'string') {
        return;
      }

      const classMappings = validClassMappingsByResource[id];

      if (
        !classMappings?.length ||
        // only add if dataSourceId was in successDetailsResponses
        !uniqueKeySetsByDataSource[dataSourceId]
      ) {
        return;
      }

      classMappings.forEach((mapping) => {
        if (mapping.uniqueKeySets.length) {
          const alphabetizedValue = mapping.uniqueKeySets
            .map((field) => `${from.value}.${field}`)
            .sort(alphaSorter)
            .join(',');
          if (!uniqueKeySetsByDataSource[dataSourceId].has(alphabetizedValue)) {
            uniqueKeySetsByDataSource[dataSourceId].add(alphabetizedValue);
            dataSourceIdsToUpdate.add(dataSourceId);
          }
        }
      });
    });

    const updateResponses = await Promise.all(
      Array.from(dataSourceIdsToUpdate).map((dataSourceId) => {
        if (!updatedDataSources.has(dataSourceId)) {
          return null;
        }

        const uniqueKeySets = uniqueKeySetsByDataSource[dataSourceId];
        if (!uniqueKeySets || !uniqueKeySets.size) {
          return null;
        }

        const deltaOptions = {
          [DataSourceProperty.UNIQUE_KEY_SETS]: Array.from(uniqueKeySets)
            .map((key) => `(${key})`)
            .join(','),
        };

        // an issue here is that trying to set duplicate keys will error here
        // since we're always using the format {schema}.{table}.{column} here, but
        // Stardog checks dupes against keys formatted like {table}.column, it's possible
        // that we miss the simple dupe checks above
        // ex: (dvdrental.public.actor.first_name,dvdrental.public.actor.last_name) vs
        // (public.actor.first_name,public.actor.last_name)
        return dataSourcesStardogRequestDispatchers.updateDataSource({
          deltaOptions,
          dataSourceId,
          force: true, // do we want to update even if the data source is used by other vgs?
        });
      })
    );

    const failUpdateResponses: UpdateFailureResponse[] = updateResponses.filter(
      (response): response is UpdateFailureResponse =>
        !!response &&
        response.type === DataSourcesActionType.UPDATE_DATA_SOURCE_FAILURE
    );
    const successUpdateResponses: UpdateSuccessResponse[] =
      updateResponses.filter(
        (response): response is UpdateSuccessResponse =>
          !!response &&
          response.type === DataSourcesActionType.UPDATE_DATA_SOURCE_SUCCESS
      );

    const responses = [
      ...failDetailsResponses.map<PublishProjectResponse>((action) => ({
        action,
        actionDescription: copy.notifications.dataSources.requestDetails,
        success: false,
        message: copy.notifications.publishProject.updateUniqueKeySets.failure(
          action.payload.action.dataSourceId
        ),
      })),
      ...failUpdateResponses.map<PublishProjectResponse>((action) => ({
        action,
        actionDescription: copy.notifications.dataSources.update,
        success: false,
        // using the same error message for details/update, but there shouldn't be duplicates
        message: copy.notifications.publishProject.updateUniqueKeySets.failure(
          action.payload.action.dataSourceId
        ),
      })),
      ...successUpdateResponses.map<PublishProjectResponse>((action) => ({
        action,
        success: true,
        message: copy.notifications.publishProject.updateUniqueKeySets.success(
          action.payload.action.dataSourceId
        ),
      })),
    ];

    dispatch(
      projectsActionCreators.publishUniqueKeySetsFinished({
        responses,
      })
    );
  };
